import React, { useEffect } from 'react';
import { GraduationCap, Globe, Code, Brain, Award, Users, CheckCircle, Share2, CreditCard } from 'lucide-react';
import Navbar from '../components/Navabar';
import Footer from '../components/Footer';
import { getCurrentLanguage } from '../utils/translations';
import usePageTitle from '../hooks/usePageTitle';

const AHBEducationPage = () => {
  const currentLanguage = getCurrentLanguage();
  usePageTitle('AHB Education - Programme de Formation Digitale');

  // Charger le script Donorbox
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://donorbox.org/widget.js';
    script.type = 'text/javascript';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  const content = {
    fr: {
      title: 'AHB EDUCATION',
      subtitle: 'Programme International de Formation Digitale',
      callTitle: 'Appel à Candidatures',
      intro: "Dans le cadre du partenariat stratégique entre Action-Humanitaire Bénin et Million Coders, nous lançons un appel à candidatures destiné aux jeunes désireux de renforcer leurs compétences numériques et de s'insérer dans l'économie digitale mondiale.",
      
      programTitle: 'Domaines de Formation',
      programDesc: 'Les formations, entièrement dispensées en ligne, couvrent les domaines suivants :',
      domains: [
        { icon: Code, title: 'Développement Web', desc: 'Maîtrisez les technologies web modernes et créez des applications performantes' },
        { icon: Brain, title: 'Intelligence Artificielle', desc: 'Explorez le machine learning et les applications de l\'IA' },
        { icon: Globe, title: 'Programmation', desc: 'Apprenez les langages de programmation les plus demandés' },
        { icon: Award, title: 'Compétences Numériques Professionnelles', desc: 'Développez vos soft skills digitaux pour le monde professionnel' }
      ],

      conditionsTitle: 'Conditions de Participation',
      conditions: [
        {
          title: 'Maîtrise Linguistique',
          desc: 'Les cours sont actuellement disponibles en anglais et en arabe. Une bonne compréhension de l\'une de ces langues est requise.'
        },
        {
          title: 'Motivation et Engagement',
          desc: 'Les candidats doivent démontrer une réelle motivation pour l\'apprentissage digital et s\'engager à suivre le programme avec assiduité.'
        }
      ],

      processTitle: 'Modalités d\'Accès',
      processSteps: [
        {
          step: '1',
          title: 'Soumission de Candidature',
          desc: 'Transmettez vos informations (nom, prénom, email, téléphone, motivation) à Action-Humanitaire Bénin.'
        },
        {
          step: '2',
          title: 'Étude et Validation',
          desc: 'Notre équipe étudie votre candidature et valide votre éligibilité au programme.'
        },
        {
          step: '3',
          title: 'Inscription sur la Plateforme',
          desc: 'Les candidats retenus sont inscrits par Million Coders qui leur envoie les accès à la plateforme de formation.'
        },
        {
          step: '4',
          title: 'Début de la Formation',
          desc: 'Recevez vos identifiants par email et commencez votre parcours d\'apprentissage digital.'
        }
      ],

      contactTitle: 'Contactez-nous',
      contactDesc: 'Pour toute information complémentaire ou pour soumettre votre candidature :',
      contactEmail: 'contact@ahbenin.org',
      contactPhone: '+229 01 55 07 81 81',

      partnerTitle: 'Notre Partenaire',
      partnerDesc: 'Million Coders est une plateforme internationale de formation digitale reconnue, offrant des cours de qualité professionnelle.',

      registrationTitle: 'Inscription et Frais de Formation',
      registrationFee: 'Frais de formation : 50 €',
      registrationIntro: 'Pour participer au programme AHB Éducation, chaque apprenant est invité à s\'acquitter des frais de formation de 50 €.',
      registrationBenefits: [
        'Une formation de qualité orientée vers les compétences d\'avenir',
        'Des contenus pratiques dans les domaines du numérique, de l\'intelligence artificielle, de la technologie et du développement professionnel',
        'Des certifications reconnues à l\'international',
        'L\'éligibilité à des opportunités de bourses pour les apprenants les plus performants',
        'Un apprentissage axé sur la pratique et l\'employabilité',
        'L\'accès aux opportunités développées dans le cadre des partenariats éducatifs de AHB Éducation'
      ],
      paymentMethodsTitle: 'Modalités de paiement',
      paymentOnline: 'Pour la majorité des pays africains, les frais de formation peuvent être réglés directement en ligne via notre plateforme sécurisée.',
      paymentNigeriaTitle: 'Cas particulier du Nigeria',
      paymentNigeriaDesc: 'En raison de certaines contraintes liées aux paiements internationaux, les apprenants résidant au Nigeria peuvent effectuer leur paiement en Naira (NGN). Le montant correspondant à 50 € sera converti en monnaie locale selon le taux de change en vigueur au moment de l\'inscription et communiqué au candidat. Le paiement sera effectué sur le compte officiel de Action-Humanitaire Bénin.',
      paymentOtherCountries: 'Pour tous les autres pays, le règlement des frais de formation s\'effectue exclusivement via la plateforme de paiement en ligne.',
      commitmentTitle: 'Notre engagement',
      commitmentDesc: 'AHB Éducation a pour mission de rendre l\'éducation numérique, les compétences technologiques et les opportunités de développement professionnel accessibles aux jeunes et aux communautés africaines. Nous croyons qu\'une formation de qualité constitue un levier essentiel pour l\'emploi, l\'innovation et le développement durable du continent.',
      ctaText: 'Investissez dans vos compétences aujourd\'hui pour construire les opportunités de demain.',
      registerButton: 'S\'inscrire maintenant',

      shareTitle: 'Partagez cette opportunité',
      shareDesc: 'Aidez d\'autres jeunes à découvrir cette opportunité de formation'
    },
    en: {
      title: 'AHB EDUCATION',
      subtitle: 'International Digital Training Program',
      callTitle: 'Call for Applications',
      intro: "As part of the strategic partnership between Action-Humanitaire Bénin and Million Coders, we are launching a call for applications for young people wishing to strengthen their digital skills and integrate into the global digital economy.",
      
      programTitle: 'Training Areas',
      programDesc: 'The training, entirely delivered online, covers the following areas:',
      domains: [
        { icon: Code, title: 'Web Development', desc: 'Master modern web technologies and create high-performance applications' },
        { icon: Brain, title: 'Artificial Intelligence', desc: 'Explore machine learning and AI applications' },
        { icon: Globe, title: 'Programming', desc: 'Learn the most in-demand programming languages' },
        { icon: Award, title: 'Professional Digital Skills', desc: 'Develop your digital soft skills for the professional world' }
      ],

      conditionsTitle: 'Participation Conditions',
      conditions: [
        {
          title: 'Language Proficiency',
          desc: 'Courses are currently available in English and Arabic. Good understanding of one of these languages is required.'
        },
        {
          title: 'Motivation and Commitment',
          desc: 'Candidates must demonstrate real motivation for digital learning and commit to following the program diligently.'
        }
      ],

      processTitle: 'Access Procedures',
      processSteps: [
        {
          step: '1',
          title: 'Application Submission',
          desc: 'Submit your information (name, email, phone, motivation) to Action-Humanitaire Bénin.'
        },
        {
          step: '2',
          title: 'Review and Validation',
          desc: 'Our team reviews your application and validates your eligibility for the program.'
        },
        {
          step: '3',
          title: 'Platform Registration',
          desc: 'Selected candidates are registered by Million Coders who send them access to the training platform.'
        },
        {
          step: '4',
          title: 'Training Start',
          desc: 'Receive your credentials by email and begin your digital learning journey.'
        }
      ],

      contactTitle: 'Contact Us',
      contactDesc: 'For more information or to submit your application:',
      contactEmail: 'contact@ahbenin.org',
      contactPhone: '+229 01 55 07 81 81',

      partnerTitle: 'Our Partner',
      partnerDesc: 'Million Coders is a recognized international digital training platform, offering professional quality courses.',

      registrationTitle: 'Registration and Training Fees',
      registrationFee: 'Training fee: €50',
      registrationIntro: 'To participate in the AHB Education Program, learners are required to pay the training fee of €50.',
      registrationBenefits: [
        'High-quality training programs and learning resources',
        'Internationally recognized certificates upon successful completion',
        'Eligibility for scholarship opportunities for outstanding learners',
        'Practical, skills-focused learning experience',
        'Access to educational opportunities developed through AHB Education partnerships'
      ],
      paymentMethodsTitle: 'Payment Information',
      paymentOnline: 'For learners from most African countries, payment can be made securely online through our platform.',
      paymentNigeriaTitle: 'Special Payment Arrangement for Nigeria',
      paymentNigeriaDesc: 'Due to payment challenges that may affect some participants in Nigeria, learners residing in Nigeria may pay the equivalent of €50 in Nigerian Naira (NGN) through the official bank account of Action-Humanitaire Bénin. The amount payable will be communicated in Naira according to the applicable exchange rate at the time of registration.',
      paymentOtherCountries: 'For all other countries, payment must be completed through the online payment platform.',
      commitmentTitle: 'Our Commitment',
      commitmentDesc: 'AHB Education is committed to making quality education accessible to learners across Africa while ensuring a simple and reliable registration process. We believe that quality training is an essential lever for employment, innovation and sustainable development of the continent.',
      ctaText: 'Invest in your skills today to build the opportunities of tomorrow.',
      registerButton: 'Register Now'
    }
  };

  const t = (key) => content[currentLanguage === 'en' ? 'en' : 'fr'][key];

  const handleShare = () => {
    const shareText = currentLanguage === 'fr' 
      ? 'AHB EDUCATION - Programme International de Formation Digitale'
      : 'AHB EDUCATION - International Digital Training Program';
    
    if (navigator.share) {
      navigator.share({
        title: 'AHB EDUCATION',
        text: shareText,
        url: window.location.href
      }).catch(err => console.log('Error sharing:', err));
    } else {
      // Fallback: copier le texte avec le lien
      const textToCopy = `${shareText}\n${window.location.href}`;
      navigator.clipboard.writeText(textToCopy);
      alert(currentLanguage === 'fr' ? 'Lien copié !' : 'Link copied!');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-[#089556] via-[#0a7c4a] to-[#089556] py-12">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="relative z-10 max-w-6xl mx-auto px-4 text-center">
          <GraduationCap className="h-16 w-16 text-white/80 mx-auto mb-4" />
          <h1 className="text-4xl md:text-5xl font-black text-white tracking-tight drop-shadow-2xl mb-3">
            {t('title')}
          </h1>
          <p className="text-xl text-white/90 font-semibold">
            {t('subtitle')}
          </p>
          <div className="w-24 h-1 bg-white/60 mx-auto mt-4 rounded-full"></div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-12">

        {/* Call to Action Banner */}
        <div className="bg-gradient-to-r from-[#07D1EB] to-[#089556] rounded-2xl p-8 mb-12 text-white shadow-2xl">
          <h2 className="text-3xl font-bold mb-4 flex items-center gap-3">
            <Users className="h-8 w-8" />
            {t('callTitle')}
          </h2>
          <p className="text-lg leading-relaxed">
            {t('intro')}
          </p>
        </div>

        {/* Affiche selon la langue */}
        <div className="mb-12">
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden max-w-2xl mx-auto">
            <img 
              src={currentLanguage === 'fr' ? '/affiche_fr.png' : '/affiche_eng.png'}
              alt={currentLanguage === 'fr' ? 'Affiche Formation - Français' : 'Training Poster - English'}
              className="w-full h-auto object-contain"
            />
          </div>
        </div>

        {/* Domaines de Formation */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-[#089556] mb-4">{t('programTitle')}</h2>
          <p className="text-gray-600 mb-8">{t('programDesc')}</p>
          <div className="grid md:grid-cols-2 gap-6">
            {t('domains').map((domain, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-shadow duration-300 border-l-4 border-[#089556]">
                <domain.icon className="h-12 w-12 text-[#089556] mb-4" />
                <h3 className="text-xl font-bold text-gray-800 mb-2">{domain.title}</h3>
                <p className="text-gray-600">{domain.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Conditions de Participation */}
        <section className="mb-12 bg-white rounded-2xl p-8 shadow-lg">
          <h2 className="text-3xl font-bold text-[#089556] mb-6">{t('conditionsTitle')}</h2>
          <div className="space-y-6">
            {t('conditions').map((condition, index) => (
              <div key={index} className="flex gap-4">
                <CheckCircle className="h-6 w-6 text-[#089556] flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-lg font-bold text-gray-800 mb-2">{condition.title}</h3>
                  <p className="text-gray-600">{condition.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Modalités d'Accès */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-[#089556] mb-8">{t('processTitle')}</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {t('processSteps').map((step, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 relative">
                <div className="absolute -top-4 -left-4 w-12 h-12 bg-gradient-to-br from-[#089556] to-[#07D1EB] rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg">
                  {step.step}
                </div>
                <h3 className="text-lg font-bold text-gray-800 mb-3 mt-4">{step.title}</h3>
                <p className="text-gray-600 text-sm">{step.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Registration and Training Fees Section */}
        <section className="mb-12">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-[#089556] mb-4">{t('registrationTitle')}</h2>
            <div className="inline-block bg-gradient-to-r from-[#089556] to-[#07D1EB] text-white px-6 py-3 rounded-full text-xl font-bold">
              {t('registrationFee')}
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 items-start">
            {/* Benefits and Payment Info */}
            <div className="space-y-6">
              {/* Benefits */}
              <div className="bg-white rounded-2xl shadow-lg p-8">
                <p className="text-gray-700 text-lg mb-6">{t('registrationIntro')}</p>
                <h3 className="text-xl font-bold text-[#089556] mb-4">
                  {currentLanguage === 'fr' ? 'Ces frais donnent accès à :' : 'This fee grants access to:'}
                </h3>
                <div className="space-y-3">
                  {t('registrationBenefits').map((benefit, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-[#089556] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Payment Methods */}
              <div className="bg-white rounded-2xl shadow-lg p-8">
                <h3 className="text-2xl font-bold text-[#089556] mb-6">{t('paymentMethodsTitle')}</h3>
                
                <div className="space-y-6">
                  {/* Online Payment */}
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-gradient-to-br from-[#089556] to-[#07D1EB] rounded-xl flex-shrink-0">
                      <CreditCard className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <p className="text-gray-700">{t('paymentOnline')}</p>
                    </div>
                  </div>

                  {/* Nigeria Special */}
                  <div className="bg-amber-50 border-l-4 border-amber-500 rounded-r-xl p-6">
                    <h4 className="text-lg font-bold text-amber-700 mb-2">{t('paymentNigeriaTitle')}</h4>
                    <p className="text-gray-700">{t('paymentNigeriaDesc')}</p>
                  </div>

                  {/* Other Countries */}
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-gray-100 rounded-xl flex-shrink-0">
                      <Globe className="h-6 w-6 text-[#089556]" />
                    </div>
                    <div>
                      <p className="text-gray-700">{t('paymentOtherCountries')}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Donorbox Payment Form and Commitment */}
            <div className="space-y-6">
              {/* Donorbox Form */}
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 bg-gradient-to-br from-[#089556] to-[#07D1EB] rounded-xl">
                    <CreditCard className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-800">
                      {currentLanguage === 'fr' ? 'Payer en ligne' : 'Pay Online'}
                    </h3>
                    <p className="text-sm text-gray-600">50 €</p>
                  </div>
                </div>
                <p className="text-gray-600 mb-6 text-sm">
                  {currentLanguage === 'fr'
                    ? 'Payez vos frais de formation en ligne de manière sécurisée via Donorbox. Le paiement sera automatiquement converti en euros.'
                    : 'Pay your training fees online securely via Donorbox. The payment will be automatically converted to euros.'}
                </p>
                <div className="bg-gray-50 rounded-xl overflow-hidden">
                  <iframe
                    src="https://donorbox.org/embed/action-humanitaire-benin?default_interval=o"
                    name="donorbox"
                    allowpaymentrequest="allowpaymentrequest"
                    seamless="seamless"
                    frameBorder="0"
                    scrolling="no"
                    height="600px"
                    width="100%"
                    style={{
                      maxWidth: '100%',
                      minWidth: '100%',
                      maxHeight: 'none',
                      border: 'none'
                    }}
                    allow="payment"
                    title="Donorbox Payment Form"
                  ></iframe>
                </div>
              </div>

              {/* Commitment */}
              <div className="bg-gradient-to-br from-[#089556] to-[#07D1EB] rounded-2xl shadow-lg p-8 text-white">
                <h3 className="text-2xl font-bold mb-4">{t('commitmentTitle')}</h3>
                <p className="text-white/90 mb-4">{t('commitmentDesc')}</p>
                <p className="text-white font-semibold text-lg">{t('ctaText')}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Share Section */}
        <section className="bg-gradient-to-r from-[#089556] to-[#07D1EB] rounded-2xl p-8 text-white text-center">
          <Share2 className="h-12 w-12 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">{t('shareTitle')}</h2>
          <p className="mb-6">{t('shareDesc')}</p>
          <button
            onClick={handleShare}
            className="bg-white text-[#089556] px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-300 shadow-lg"
          >
            {currentLanguage === 'fr' ? 'Partager' : 'Share'}
          </button>
        </section>

      </main>

      <Footer />
    </div>
  );
};

export default AHBEducationPage;
